import debug from 'debug';

import { purgeTrackingArtifacts } from './purgeTrackingArtifacts';

const debugLog = debug('global:consent');

/**
 * A consent selection: integration display name → granted.
 *
 * Intentionally looser than `IntegrationData`. The funnel reuses that type both for the
 * integration *config* coming from layout data (`{ Perspective: { apiUrl } }`) and for the
 * *selection* the banner produces (`{ Perspective: true }`). The gate only cares whether a
 * value is truthy, so it accepts either shape.
 */
export type ConsentSelection = Record<string, unknown>;

/**
 * Coerce a stored/incoming selection into a plain object.
 *
 * Preferences are `JSON.parse`d out of localStorage, so at runtime they can be anything a
 * corrupted, hand-edited or half-written entry contains — `null`, a string, an array.
 * Anything that isn't a plain object is treated as "no decision recorded", which leaves the
 * gate closed so the banner asks again. Without this a stored `null` threw out of
 * `Object.keys()` during layout init and took the whole funnel down with it.
 */
const asSelection = (value: unknown): ConsentSelection =>
    typeof value === 'object' && value !== null && !Array.isArray(value)
        ? (value as ConsentSelection)
        : {};

/**
 * Display name of Perspective's own (first-party) analytics integration.
 *
 * Mirrors `integrationsToDisplayName[integrations.perspective]`, duplicated here on
 * purpose: the gate must be importable from the tracking layer without importing the
 * tracking layer back (it is consulted from `tracking/helper.shouldTrack`).
 */
export const PERSPECTIVE_INTEGRATION = 'Perspective';

/**
 * CAP-4147 / § 25 TDDDG: Perspective's own funnel analytics is **not** strictly necessary
 * for the funnel to function. It sets persistent identifiers (`ajs_anonymous_id`,
 * `ajs_user_id`, `perspective__user__id`) purely for measurement, so it requires opt-in
 * consent just like any third-party integration.
 *
 * Flip to `false` only if the classification is revisited (see CAP-4042) — the rest of the
 * gate keeps working unchanged, "Reject all" would then still allow first-party analytics.
 */
export const PERSPECTIVE_REQUIRES_CONSENT = true;

export type ConsentStatus = 'pending' | 'granted' | 'denied';

let status: ConsentStatus = 'pending';
let listeners: Array<() => void> = [];

export const getConsentStatus = (): ConsentStatus => status;

/**
 * The single source of truth for "may we touch persistent storage / send an event?".
 *
 * Fails closed: until something explicitly resolves the gate, nothing is allowed.
 */
export const isTrackingAllowed = (): boolean => status === 'granted';

/**
 * Run `callback` as soon as tracking is allowed.
 *
 * Runs synchronously when consent is already granted. While pending *or denied* the
 * callback stays queued, so a visitor who first rejects and later opts in via
 * "Manage Cookies" still gets a working tracking stack without a page reload.
 */
export const onConsentGranted = (callback: () => void): void => {
    if (status === 'granted') {
        callback();

        return;
    }

    listeners.push(callback);
};

export const grantConsent = (): void => {
    if (status === 'granted') return;

    debugLog('-> consent granted');
    status = 'granted';

    // Snapshot first: a callback may register another callback.
    const queued = listeners;
    listeners = [];
    queued.forEach((callback) => callback());
};

export const denyConsent = (): void => {
    debugLog('-> consent denied');
    status = 'denied';
    purgeTrackingArtifacts();
};

/**
 * Resolve the gate from an explicit consent selection — either a banner interaction or
 * preferences restored from a previous visit.
 */
export const applyConsentSelection = (selection: ConsentSelection): void => {
    const normalized = asSelection(selection);
    const perspectiveAllowed =
        !PERSPECTIVE_REQUIRES_CONSENT || Boolean(normalized[PERSPECTIVE_INTEGRATION]);

    if (perspectiveAllowed) {
        grantConsent();
    } else {
        denyConsent();
    }
};

interface ConsentGateOptions {
    /** `data.cookieOverlay.layout` — 'none' means the funnel has no consent banner. */
    bannerLayout: string;
    /** `embedOptions.cookieBanner` — false when the embedding host owns consent. */
    bannerEnabled: boolean;
    /** `embedOptions.preview` — the editor preview never delivers events. */
    isPreview: boolean;
    /**
     * Preferences persisted by a previous visit, from `getCookiePreferences()`. Typed as
     * `unknown` on purpose: it is parsed from localStorage and cannot be trusted to be a
     * well-formed selection.
     */
    storedPreferences: unknown;
}

/**
 * Decide the gate's starting state. Must run before the tracking service is created.
 *
 * Only the last branch leaves the gate closed — that is the case CAP-4147 is about: a
 * banner is shown, the visitor has not answered it yet, so nothing may be stored or sent.
 */
export const initConsentGate = ({
    bannerLayout,
    bannerEnabled,
    isPreview,
    storedPreferences,
}: ConsentGateOptions): void => {
    resetConsentGate();

    // Preview mode is gated separately in shouldTrack(), which never sends anything.
    // Opening the gate keeps preview behaviour (session ids, embed variables) intact.
    if (isPreview) {
        debugLog('-> gate open: preview mode');
        grantConsent();

        return;
    }

    // Embedded without our banner — the host page is responsible for collecting consent.
    if (!bannerEnabled) {
        debugLog('-> gate open: cookie banner disabled by embed options');
        grantConsent();

        return;
    }

    // The funnel has no consent banner configured at all.
    if (bannerLayout === 'none') {
        debugLog('-> gate open: no banner configured');
        grantConsent();

        return;
    }

    const preferences = asSelection(storedPreferences);

    if (Object.keys(preferences).length > 0) {
        debugLog('-> resolving gate from stored preferences', preferences);
        applyConsentSelection(preferences);

        return;
    }

    debugLog('-> gate closed: awaiting banner interaction');
};

/** Test seam — resets module state between cases. */
export const resetConsentGate = (): void => {
    status = 'pending';
    listeners = [];
};
